import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Settings, Shield, Bell, Church, Upload, Save, UserPlus, Copy, Trash2, Mail, BookOpen, RefreshCw, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useFreemium } from "@/hooks/useFreemium";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import RolePermissionsPanel from "@/components/settings/RolePermissionsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

type InviteRoleValue = "admin" | "staff" | "volunteer";

type NotificationPrefs = {
  new_member: boolean;
  birthday: boolean;
  team_updates: boolean;
  data_alerts: boolean;
  finance: boolean;
};

const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  new_member: true,
  birthday: true,
  team_updates: true,
  data_alerts: true,
  finance: true,
};


const SettingsPage = () => {
  const { profile, updateProfile, uploadLogo } = useChurch();
  const { role, user } = useAuth();
  const { can, isChurchAdmin } = usePermissions();
  const { isPreview, requireSubscription } = useFreemium();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const notificationPrefsKey = user?.id ? `notification_prefs_${user.id}` : null;
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);


  const [form, setForm] = useState({
    church_name: "", abbreviation: "", address: "", city: "", state: "", country: "", zip_code: "", phone: "", email: "",
    bible_verse: "", bible_reference: "",
  });

  // Auto-generate abbreviation from church name
  const generateAbbreviation = (name: string) => {
    return name.split(/\s+/).filter(w => w.length > 0).map(w => w[0].toUpperCase()).join("");
  };

  useEffect(() => {
    if (profile) {
      setForm({
        church_name: profile.church_name, abbreviation: profile.abbreviation || generateAbbreviation(profile.church_name),
        address: profile.address || "",
        city: profile.city || "", state: profile.state || "",
        country: profile.country || "", zip_code: profile.zip_code || "",
        phone: profile.phone || "", email: profile.email || "",
        bible_verse: profile.bible_verse || "", bible_reference: profile.bible_reference || "",
      });
    }
  }, [profile]);

  useEffect(() => {
    if (!notificationPrefsKey || typeof window === "undefined") return;

    try {
      const raw = window.localStorage.getItem(notificationPrefsKey);
      if (!raw) {
        setNotificationPrefs(DEFAULT_NOTIFICATION_PREFS);
        return;
      }
      const parsed = JSON.parse(raw) as Partial<NotificationPrefs>;
      setNotificationPrefs({ ...DEFAULT_NOTIFICATION_PREFS, ...parsed });
    } catch {
      setNotificationPrefs(DEFAULT_NOTIFICATION_PREFS);
    }
  }, [notificationPrefsKey]);

  const updateNotificationPref = (key: keyof NotificationPrefs, value: boolean) => {
    setNotificationPrefs((prev) => {
      const next = { ...prev, [key]: value };
      if (notificationPrefsKey && typeof window !== "undefined") {
        window.localStorage.setItem(notificationPrefsKey, JSON.stringify(next));
        window.dispatchEvent(new Event("notification-prefs-updated"));
      }
      return next;
    });
  };

  // Team members
  const { data: teamMembers = [] } = useQuery({
    queryKey: ["team_members", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from("user_roles").select("*").eq("church_id", profile!.id);
      if (error) throw error;
      // Guard against accidental duplicate rows for the same user.
      const uniqueByUser = new Map<string, any>();
      (data || []).forEach((row: any) => {
        const existing = uniqueByUser.get(row.user_id);
        if (!existing) {
          uniqueByUser.set(row.user_id, row);
          return;
        }

        // Prefer rows with a role_id, then most recently created by id as fallback.
        const shouldReplace = (!existing.role_id && row.role_id) || String(row.id) > String(existing.id);
        if (shouldReplace) uniqueByUser.set(row.user_id, row);
      });

      const dedupedRows = Array.from(uniqueByUser.values());
      const userIds = dedupedRows.map((r: any) => r.user_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds);
      const profileMap: Record<string, string> = {};
      (profiles || []).forEach((p: any) => {
        const display = String(p.display_name || "").trim();
        if (display) profileMap[p.user_id] = display;
      });
      // Also fetch church_role names
      const roleIds = dedupedRows.map((r: any) => r.role_id).filter(Boolean);
      let roleNameMap: Record<string, string> = {};
      if (roleIds.length > 0) {
        const { data: roles } = await (supabase as any).from("church_roles").select("id, name").in("id", roleIds);
        (roles || []).forEach((r: any) => { roleNameMap[r.id] = r.name; });
      }
      // Build email map: current user's email from auth, others from accepted invitations
      const emailMap: Record<string, string> = {};
      // Current user email is known from auth session
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) emailMap[currentUser.id] = currentUser.email || "";
      // For other team members, match via accepted invitations
      const otherUserIds = userIds.filter((uid: string) => uid !== currentUser?.id);
      if (otherUserIds.length > 0) {
        const { data: invData } = await supabase
          .from("invitations")
          .select("email, accepted_at, role, role_id")
          .eq("church_id", profile!.id)
          .not("accepted_at", "is", null)
          .order("accepted_at", { ascending: false });

        // Use at most one accepted invitation per email to avoid displaying the same
        // email on multiple users when historical accepted invites exist.
        const uniqueInvites: any[] = [];
        const seenInviteEmails = new Set<string>();
        (invData || []).forEach((inv: any) => {
          const normalized = String(inv.email || "").trim().toLowerCase();
          if (!normalized || seenInviteEmails.has(normalized)) return;
          seenInviteEmails.add(normalized);
          uniqueInvites.push(inv);
        });

        // Match each accepted invitation to an unassigned team member by role_id.
        const assignedUserIds = new Set<string>();
        const usedInviteIndexes = new Set<number>();
        uniqueInvites.forEach((inv: any, idx: number) => {
          for (const r of dedupedRows) {
            if (!emailMap[r.user_id] && !assignedUserIds.has(r.user_id) && r.user_id !== currentUser?.id) {
              if (inv.role_id && r.role_id === inv.role_id) {
                emailMap[r.user_id] = inv.email;
                assignedUserIds.add(r.user_id);
                usedInviteIndexes.add(idx);
                break;
              }
            }
          }
        });

        // Second pass: assign remaining by base role string when available.
        uniqueInvites.forEach((inv: any, idx: number) => {
          if (usedInviteIndexes.has(idx)) return;
          for (const r of dedupedRows) {
            if (!emailMap[r.user_id] && !assignedUserIds.has(r.user_id) && r.user_id !== currentUser?.id) {
              if (inv.role && r.role && inv.role === r.role) {
                emailMap[r.user_id] = inv.email;
                assignedUserIds.add(r.user_id);
                usedInviteIndexes.add(idx);
                break;
              }
            }
          }
        });
      }

      return dedupedRows.map((r: any) => ({
        ...r,
        display_name: profileMap[r.user_id] || "",
        email: emailMap[r.user_id] || "",
        role_name: r.role_id ? roleNameMap[r.role_id] || r.role : r.role,
      }));
    },
    enabled: !!profile && (isChurchAdmin || can("can_manage_users")),
  });

  // Church roles (for invite selector)
  const { data: churchRoles = [] } = useQuery({
    queryKey: ["church_roles", profile?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("church_roles")
        .select("*")
        .eq("church_id", profile!.id)
        .order("name");
      if (error) throw error;
      return data || [];
    },
    enabled: !!profile?.id,
  });

  // Legacy custom roles (keep for backward compat)
  const { data: customRoles = [] } = useQuery({
    queryKey: ["custom_roles", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_roles")
        .select("*")
        .eq("church_id", profile!.id)
        .order("name");
      if (error) throw error;
      return data || [];
    },
    enabled: !!profile?.id,
  });

  // Invitations
  const { data: invitations = [] } = useQuery({
    queryKey: ["invitations", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invitations")
        .select("*")
        .eq("church_id", profile!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!profile && (isChurchAdmin || can("can_manage_users")),
  });

  const activePendingInvitations = useMemo(
    () => invitations.filter((inv: any) => !inv.accepted_at && new Date(inv.expires_at).getTime() > Date.now()),
    [invitations]
  );

  const latestPendingInviteId = useMemo(
    () => (activePendingInvitations[0]?.id as string | undefined),
    [activePendingInvitations]
  );

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("");

  const inviteRoleOptions = useMemo(() => {
    return churchRoles.map((cr: any) => {
      const systemLevel = String(cr.system_level || "custom").toLowerCase();
      const invitationRole: InviteRoleValue =
        systemLevel === "church_admin" ? "admin" :
        systemLevel === "volunteer" ? "volunteer" :
        "staff";

      return {
        value: cr.id as string,
        label: cr.name as string,
        invitationRole,
        churchRoleId: cr.id as string,
        roleName: cr.name as string,
      };
    });
  }, [churchRoles]);

  const adminRoleIdSet = useMemo(() => {
    return new Set(
      churchRoles
        .filter((cr: any) => String(cr.system_level || "").toLowerCase() === "church_admin")
        .map((cr: any) => cr.id)
    );
  }, [churchRoles]);

  const sortedTeamMembers = useMemo(() => {
    const isAdminMember = (member: any) => {
      const role = String(member.role || "").toLowerCase();
      const roleName = String(member.role_name || "").toLowerCase();
      return role === "admin" || roleName.includes("admin") || (!!member.role_id && adminRoleIdSet.has(member.role_id));
    };

    return [...teamMembers].map((member: any) => {
      const fallbackName = member.display_name?.trim()
        || (member.email ? String(member.email).split("@")[0] : "")
        || "Unknown";

      return {
        ...member,
        display_name: fallbackName,
      };
    }).sort((a: any, b: any) => {
      const aIsAdmin = isAdminMember(a);
      const bIsAdmin = isAdminMember(b);
      if (aIsAdmin !== bIsAdmin) return aIsAdmin ? -1 : 1;
      return String(a.display_name || "").localeCompare(String(b.display_name || ""));
    });
  }, [teamMembers, adminRoleIdSet]);

  const currentTeamRows = useMemo(() => {
    const rows: Array<any> = sortedTeamMembers.map((member: any) => ({
      ...member,
      row_type: "member",
      status_label: "Active",
    }));

    const existingEmails = new Set(
      rows
        .map((r) => String(r.email || "").trim().toLowerCase())
        .filter(Boolean)
    );

    activePendingInvitations.forEach((inv: any) => {
      const normalizedEmail = String(inv.email || "").trim().toLowerCase();
      if (!normalizedEmail || existingEmails.has(normalizedEmail)) return;

      rows.push({
        id: `invite-${inv.id}`,
        row_type: "invite",
        user_id: null,
        display_name: normalizedEmail.split("@")[0] || "Invited User",
        email: inv.email,
        role_name: inv.role_name || inv.role || "staff",
        role_id: inv.role_id || "",
        invitation_id: inv.id,
        token: inv.token,
        status_label: "Sent",
      });
    });

    return rows;
  }, [sortedTeamMembers, activePendingInvitations]);

  useEffect(() => {
    if (!inviteRoleOptions.some((opt) => opt.value === inviteRole)) {
      const defaultOption = inviteRoleOptions[0];
      if (defaultOption) setInviteRole(defaultOption.value);
      else setInviteRole("");
    }
  }, [inviteRoleOptions, inviteRole]);

  const getRecipientAccountState = async (email: string, role: InviteRoleValue) => {
    const { data: checkData, error: checkError } = await supabase.functions.invoke("invite-user", {
      body: {
        email,
        role,
        checkOnly: true,
      },
    });

    if (checkError || (checkData?.error && !checkData?.success)) {
      throw new Error(checkData?.error || checkError?.message || "Failed to validate invite recipient");
    }

    return {
      existingUser: !!(checkData?.existingUser || checkData?.code === "USER_ALREADY_HAS_ACCOUNT"),
      hasChurch: !!checkData?.hasChurch,
    };
  };

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const selectedRoleOption = inviteRoleOptions.find((opt) => opt.value === inviteRole);
      if (!selectedRoleOption) {
        throw new Error("Please select a valid role");
      }

      const trimmedEmail = inviteEmail.trim().toLowerCase();

      const accountState = await getRecipientAccountState(trimmedEmail, selectedRoleOption.invitationRole);
      // Accounts without a church (e.g. revoked members) can be re-invited.
      if (accountState.existingUser && accountState.hasChurch) {
        throw new Error("This user already belongs to a church. Ask them to sign in instead.");
      }

      const { data: existingInvites, error: existingInvitesError } = await supabase
        .from("invitations")
        .select("id")
        .eq("church_id", profile!.id)
        .eq("email", trimmedEmail)
        .is("accepted_at", null)
        .gt("expires_at", new Date().toISOString())
        .limit(1);

      if (existingInvitesError) throw existingInvitesError;
      if ((existingInvites?.length || 0) > 0) {
        throw new Error("This user is already invited.");
      }

      // Keep a pending invitation row for in-app tracking and acceptance flow.
      const { data: inv, error } = await supabase.from("invitations").insert({
        church_id: profile!.id,
        email: trimmedEmail,
        role: selectedRoleOption.invitationRole,
        role_id: selectedRoleOption.churchRoleId,
        role_name: selectedRoleOption.roleName,
        invited_by: user?.id,
      }).select("id, token").single();
      if (error) throw error;

      // Email the branded invite link (/invite?token=...) — valid 7 days, not single-use
      const { data: fnData, error: fnError } = await supabase.functions.invoke("send-invitation", {
        body: {
          invitation_id: inv.id,
          site_url: window.location.origin,
        },
      });

      if (fnError || (fnData?.error && !fnData?.success)) {
        const fallbackLink = `${window.location.origin}/invite?token=${inv.token}`;
        try { await navigator.clipboard.writeText(fallbackLink); } catch {}
        return {
          selectedRoleLabel: selectedRoleOption.label,
          emailSent: false,
          errorMessage: fnData?.error || fnError?.message || "Failed to send invitation",
        };
      }

      return {
        selectedRoleLabel: selectedRoleOption.label,
        emailSent: true,
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
      const roleMessage = result?.selectedRoleLabel
        ? ` as ${result.selectedRoleLabel}`
        : "";
      if (result?.emailSent === false) {
        toast.warning(
          `Invite link created for ${inviteEmail.trim()}${roleMessage}. Email delivery failed (${result?.errorMessage || "unknown error"}), so the link was copied to clipboard.`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Invitation email sent to ${inviteEmail.trim()}${roleMessage}!`);
      }
      setInviteEmail("");
      const defaultOption =
        inviteRoleOptions[0];
      setInviteRole(defaultOption?.value || "");
    },
    onError: (err: any) => toast.error(err?.message || "Failed to send invitation"),
  });

  const updateTeamRoleMutation = useMutation({
    mutationFn: async ({ userRoleId, roleId, roleName }: { userRoleId: string; roleId: string; roleName: string }) => {
      const churchRole = churchRoles.find((cr: any) => cr.id === roleId);
      const systemLevel = String(churchRole?.system_level || "custom").toLowerCase();
      const newRole = systemLevel === "church_admin" ? "admin" : systemLevel === "volunteer" ? "volunteer" : "staff";
      const { error } = await supabase
        .from("user_roles")
        .update({ role_id: roleId, role: newRole })
        .eq("id", userRoleId);
      if (error) throw error;
      return roleName;
    },
    onSuccess: (roleName) => {
      queryClient.invalidateQueries({ queryKey: ["team_members"] });
      toast.success(`Role updated to ${roleName}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const revokeTeamMemberMutation = useMutation({
    mutationFn: async (userRoleId: string) => {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("id", userRoleId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team_members"] });
      toast.success("Team member access revoked");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteInviteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("invitations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["invitations"] }); toast.success("Invitation deleted. The invite link is now expired."); },
    onError: (err: Error) => toast.error(err.message),
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (invite: any) => {
      const normalizedEmail = String(invite.email || "").trim().toLowerCase();
      if (!normalizedEmail) throw new Error("Invite email is missing");

      const accountState = await getRecipientAccountState(normalizedEmail, invite.role as InviteRoleValue);

      if (accountState.existingUser) {
        const { data: recoveryData, error: recoveryError } = await supabase.functions.invoke("invite-user", {
          body: {
            email: normalizedEmail,
            role: (invite.role as InviteRoleValue) || "staff",
            roleId: invite.role_id || null,
            churchId: profile!.id,
            invitationToken: invite.token,
            roleName: invite.role_name || null,
            sendRecovery: true,
          },
        });

        if (recoveryError || (recoveryData?.error && !recoveryData?.success)) {
          throw new Error(recoveryData?.error || recoveryError?.message || "Failed to send recovery setup link");
        }

        return {
          email: normalizedEmail,
          recoverySent: true,
        };
      }

      // Invalidate prior active links so only the newest invite token is usable.
      const { error: expireError } = await supabase
        .from("invitations")
        .update({ expires_at: new Date().toISOString() })
        .eq("church_id", profile!.id)
        .eq("email", normalizedEmail)
        .is("accepted_at", null)
        .gt("expires_at", new Date().toISOString());
      if (expireError) throw expireError;

      const { data: newInvite, error: insertError } = await supabase
        .from("invitations")
        .insert({
          church_id: profile!.id,
          email: normalizedEmail,
          role: invite.role,
          role_id: invite.role_id || null,
          role_name: invite.role_name || null,
          invited_by: user?.id,
        })
        .select("id, token")
        .single();
      if (insertError) throw insertError;

      // Email the branded invite link (/invite?token=...) — valid 7 days, not single-use
      const { data: fnData, error: fnError } = await supabase.functions.invoke("send-invitation", {
        body: {
          invitation_id: newInvite.id,
          site_url: window.location.origin,
        },
      });

      if (fnError || (fnData?.error && !fnData?.success)) {
        const fallbackLink = `${window.location.origin}/invite?token=${newInvite.token}`;
        try { await navigator.clipboard.writeText(fallbackLink); } catch {}
        return {
          email: normalizedEmail,
          emailSent: false,
          errorMessage: fnData?.error || fnError?.message || "Failed to send invitation",
        };
      }

      return {
        email: normalizedEmail,
        emailSent: true,
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
      if (result?.emailSent === false) {
        toast.warning(`Resent invite for ${result.email}. Email delivery failed (${result?.errorMessage || "unknown error"}), so the newest invite link was copied to clipboard.`, {
          duration: 6000,
        });
        return;
      }
      if (result?.recoverySent) {
        toast.success(`Recovery setup email sent to ${result?.email}. They can continue account setup from that link.`, { duration: 6000 });
        return;
      }
      toast.success(`Invite resent to ${result?.email}. Only the newest link will work.`);
    },
    onError: (err: any) => toast.error(err?.message || "Failed to resend invite"),
  });

  const handleSave = async () => {
    if (!form.church_name.trim()) { toast.error("Church name is required"); return; }
    setSaving(true);
    try {
      await updateProfile({
        church_name: form.church_name.trim(), abbreviation: form.abbreviation.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null, state: form.state.trim() || null,
        country: form.country.trim() || null, zip_code: form.zip_code.trim() || null,
        phone: form.phone.trim() || null, email: form.email.trim() || null,
        bible_verse: form.bible_verse.trim() || null, bible_reference: form.bible_reference.trim() || null,
      } as any);
      toast.success("Church profile saved!");
    } catch (err: any) { toast.error(err.message); }
    setSaving(false);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ["image/png", "image/jpeg", "image/svg+xml"];
    if (!validTypes.includes(file.type)) { toast.error("Please upload a PNG, JPG, or SVG file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setUploading(true);
    try { const url = await uploadLogo(file); await updateProfile({ logo_url: url }); toast.success("Logo uploaded!"); }
    catch (err: any) { toast.error(err.message); }
    setUploading(false);
  };

  const copyInviteLink = (token: string) => {
    if (!requireSubscription()) return;
    const url = `${window.location.origin}/invite?token=${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied!");
  };

  const isAdmin = isChurchAdmin || can("can_manage_church_settings");

  const settingsSectionCardClass = "stat-card p-4 sm:p-5 transition-none hover:translate-y-0 hover:shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] hover:border-border/60";
  const tabTriggerClass = "h-7 shrink-0 whitespace-nowrap px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground";

  return (
    <div className="space-y-4">
      <Tabs defaultValue="churchProfile" className="space-y-4">
        <div className="overflow-x-auto sm:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsList className="h-9 min-w-max rounded-none bg-muted p-1 gap-1 flex-nowrap items-center">
            <TabsTrigger value="churchProfile" className={tabTriggerClass}><Church className="w-3.5 h-3.5 mr-1.5" />Church Profile</TabsTrigger>
            <TabsTrigger value="bibleVerse" className={tabTriggerClass}><BookOpen className="w-3.5 h-3.5 mr-1.5" />Verse of Year</TabsTrigger>
            {(isChurchAdmin || can("can_manage_users")) && (
              <TabsTrigger value="teamManagement" className={tabTriggerClass}><UserPlus className="w-3.5 h-3.5 mr-1.5" />Team</TabsTrigger>
            )}
            {(isChurchAdmin || can("can_manage_roles")) && (
              <TabsTrigger value="rolePermissions" className={tabTriggerClass}><Settings className="w-3.5 h-3.5 mr-1.5" />Permissions</TabsTrigger>
            )}
            <TabsTrigger value="notifications" className={tabTriggerClass}><Bell className="w-3.5 h-3.5 mr-1.5" />Notifications</TabsTrigger>
          </TabsList>
        </div>

        {/* Church Profile */}
        <TabsContent value="churchProfile">
        <div className={settingsSectionCardClass}>
          <div className="flex items-center gap-3 mb-4"><Church className="w-5 h-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Church Profile</h3></div>
          <div className="space-y-5">
            <div className="flex items-center gap-4 mb-6">
              <Avatar className="w-16 h-16 rounded-xl border-2 border-border">
                {profile?.logo_url ? <AvatarImage src={profile.logo_url} alt="Church logo" className="object-cover" /> : null}
                <AvatarFallback className="rounded-xl bg-primary/10 text-primary"><Church className="w-7 h-7" /></AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium text-foreground">Church Logo</p>
                <p className="text-xs text-muted-foreground mb-2">PNG, JPG, or SVG (max 5MB)</p>
                {isAdmin && (
                  <><Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    <Upload className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : "Upload Logo"}
                  </Button><input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.svg" className="hidden" onChange={handleLogoUpload} /></>
                )}
              </div>
            </div>

            {isAdmin ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Church Name *</Label><Input value={form.church_name} onChange={(e) => {
                    const name = e.target.value;
                    setForm((p) => ({ ...p, church_name: name, abbreviation: p.abbreviation === generateAbbreviation(p.church_name) ? generateAbbreviation(name) : p.abbreviation }));
                  }} /></div>
                  <div className="space-y-2"><Label>Abbreviation</Label><Input value={form.abbreviation} onChange={(e) => setForm((p) => ({ ...p, abbreviation: e.target.value }))} placeholder="e.g. ICCC" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2"><Label>Street Address</Label><Input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>State / Province</Label><Input value={form.state} onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>Country</Label><Input value={form.country} onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>ZIP / Postal Code</Label><Input value={form.zip_code} onChange={(e) => setForm((p) => ({ ...p, zip_code: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} /></div>
                </div>
                <Button onClick={handleSave} disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Profile"}</Button>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                {[["Church Name", profile?.church_name], ["Address", profile?.address], ["Phone", profile?.phone], ["Email", profile?.email]].map(([label, val]) => (
                  <div key={label as string} className="flex justify-between py-2 border-b border-border/50">
                    <span className="text-muted-foreground">{label}</span><span className="font-medium text-foreground">{val || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </TabsContent>

        {/* Bible Verse */}
        <TabsContent value="bibleVerse">
        <div className={settingsSectionCardClass}>
          <div className="flex items-center gap-3 mb-4"><BookOpen className="w-5 h-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Church Verse of the Year</h3></div>
          <div>
            <p className="text-xs text-muted-foreground mb-4">This verse will be displayed on your dashboard for your team to see.</p>
            {isAdmin ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Bible Verse</Label>
                  <Textarea
                    value={form.bible_verse}
                    onChange={(e) => setForm((p) => ({ ...p, bible_verse: e.target.value }))}
                    placeholder="For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reference</Label>
                  <Input
                    value={form.bible_reference}
                    onChange={(e) => setForm((p) => ({ ...p, bible_reference: e.target.value }))}
                    placeholder="Jeremiah 29:11"
                  />
                </div>
                <Button onClick={handleSave} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Verse"}
                </Button>
              </div>
            ) : (
              <div className="py-4">
                {profile?.bible_verse ? (
                  <blockquote className="border-l-4 border-primary/30 pl-4 italic text-foreground">
                    &ldquo;{profile.bible_verse}&rdquo;
                    {profile.bible_reference && <footer className="text-sm text-muted-foreground mt-2 not-italic font-medium">— {profile.bible_reference}</footer>}
                  </blockquote>
                ) : (
                  <p className="text-sm text-muted-foreground">No verse set yet.</p>
                )}
              </div>
            )}
          </div>
        </div>
        </TabsContent>

        {/* Team Management (Admin only) */}
        {(isChurchAdmin || can("can_manage_users")) && (
        <TabsContent value="teamManagement">
        <div className={settingsSectionCardClass}>
          <div className="flex items-center gap-3 mb-4"><UserPlus className="w-5 h-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Team Management</h3></div>
          <div>

            {/* Current team */}
            <div className="space-y-3 mb-6">
              <h4 className="text-sm font-medium text-foreground">Current Team</h4>
              {currentTeamRows.map((m: any) => {
                const isYou = m.user_id === user?.id;
                const isInviteRow = m.row_type === "invite";
                return (
                  <div key={m.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-foreground">{m.display_name}</p>
                      {m.email && <p className="text-xs text-muted-foreground">{m.email}</p>}
                      <p className="text-xs text-muted-foreground">{isYou ? "(You)" : ""}</p>
                    </div>
                    {isYou ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{m.role_name || m.role}</Badge>
                        <Badge variant="outline">{m.status_label || "Active"}</Badge>
                      </div>
                    ) : isInviteRow ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{m.role_name || "staff"}</Badge>
                        <Badge variant="outline">{m.status_label || "Sent"}</Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => copyInviteLink(m.token)}
                          title="Copy invite link"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => resendInviteMutation.mutate({
                            id: m.invitation_id,
                            email: m.email,
                            role: m.role || "staff",
                            role_id: m.role_id || null,
                            role_name: m.role_name || null,
                          })}
                          title="Resend invite"
                          disabled={resendInviteMutation.isPending}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Revoke invite"
                              disabled={deleteInviteMutation.isPending}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Revoke Invitation</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to revoke the invitation for <strong>{m.email}</strong>? The invite link will no longer work and they won't be able to join using it.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => deleteInviteMutation.mutate(m.invitation_id)}
                              >
                                Revoke Invitation
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Select
                          value={m.role_id || ""}
                          onValueChange={(newRoleId) => {
                            const cr = churchRoles.find((r: any) => r.id === newRoleId);
                            if (cr) {
                              updateTeamRoleMutation.mutate({ userRoleId: m.id, roleId: newRoleId, roleName: cr.name });
                            }
                          }}
                        >
                          <SelectTrigger className="w-48 h-8 text-xs">
                            <SelectValue placeholder={m.role_name || m.role} />
                          </SelectTrigger>
                          <SelectContent>
                            {churchRoles.map((cr: any) => (
                              <SelectItem key={cr.id} value={cr.id}>{cr.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Badge variant="outline">{m.status_label || "Active"}</Badge>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Revoke Access</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to revoke access for <strong>{m.display_name}</strong>{m.email ? ` (${m.email})` : ""}? They will no longer be able to access this church.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => revokeTeamMemberMutation.mutate(m.id)}
                              >
                                Revoke Access
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Invite form */}
            <div className="space-y-3 border-t border-border pt-4">
              <h4 className="text-sm font-medium text-foreground">Invite Staff</h4>
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-2"><Label className="text-xs">Email</Label><Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="staff@example.com" /></div>
                <div className="w-64 space-y-2"><Label className="text-xs">Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}><SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      {inviteRoleOptions.map((roleOption) => (
                        <SelectItem key={roleOption.value} value={roleOption.value}>{roleOption.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" onClick={() => {
                  if (!requireSubscription()) return;
                  inviteMutation.mutate();
                }} disabled={inviteMutation.isPending || !inviteEmail.trim() || !inviteRoleOptions.length}>
                  {isPreview && <span className="mr-2"></span>}
                  <Mail className="w-4 h-4 mr-1" /> Invite
                </Button>
              </div>
              {!inviteRoleOptions.length && (
                <p className="text-xs text-muted-foreground">
                  No team roles found. Create one in Role Permissions first.
                </p>
              )}
            </div>
          </div>
        </div>
        </TabsContent>
        )}

        {/* Role Permissions (Admin only) */}
        {(isChurchAdmin || can("can_manage_roles")) && (
        <TabsContent value="rolePermissions">
        <div className={settingsSectionCardClass}>
          <div className="flex items-center gap-3 mb-4"><Settings className="w-5 h-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Role Permissions</h3></div>
          <RolePermissionsPanel />
        </div>
        </TabsContent>
        )}

        {/* Notifications */}
        <TabsContent value="notifications">
        <div className={settingsSectionCardClass}>
          <div className="flex items-center gap-3 mb-4"><Bell className="w-5 h-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Notifications</h3></div>
          <div>
            <div className="space-y-4">
              {[
                { label: "New member added", description: "Get notified when a new member is added", key: "new_member" },
                { label: "Birthdays this week", description: "See upcoming birthdays in your church", key: "birthday" },
                { label: "Team member updates", description: "Notify when member profiles are updated by your team", key: "team_updates" },
                { label: "Finance alerts", description: "Notify when a finance record is added", key: "finance" },
                { label: "Data quality alerts", description: "Missing profile field alerts (phone, email, birthday, address, ministry, and more)", key: "data_alerts" },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div><p className="text-sm font-medium text-foreground">{item.label}</p><p className="text-xs text-muted-foreground">{item.description}</p></div>
                  <Switch
                    checked={notificationPrefs[item.key as keyof NotificationPrefs]}
                    onCheckedChange={(checked) => updateNotificationPref(item.key as keyof NotificationPrefs, checked)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
